import {
  computed,
  shallowRef,
  toValue,
  watchEffect,
  getCurrentScope,
  watch,
  type MaybeRef,
  type MaybeRefOrGetter,
} from "vue";
import type Ably from "ably";
import { useQueue } from "../use-queue";
import {
  useAblyChannelRegistry,
  ablyChannelValidateIdentityFn,
  type AblyChannelValidateFn,
  type MessageReaderHandle,
} from "./ably-channel-registry";
import type { ChannelDescriptor } from "@f3/shared";
import {
  rewindChannel,
  withAblyChannelSyntheticDefaultOptions,
  type AblyChannelSyntheticOptions,
} from "./ably.types";

function makeRawDescriptor(name: string): ChannelDescriptor<never, [], "none"> {
  return {
    name: () => name,
    presence: "none",
    options: { modes: ["publish", "subscribe"] },
  };
}

/**
 * Use a lazy event emitter for Ably channels that may not be initialized yet.
 * Publish queue drains when the handle transitions to attached (INV-10).
 *
 * Supports both new API (descriptor+channelArgs) and legacy API (channelName string).
 */
export function useSocketsLazyEvents<
  T,
  D extends ChannelDescriptor<any, any[], any> = ChannelDescriptor<
    never,
    [],
    "none"
  >,
>(args: {
  descriptor?: D;
  /** May be a getter/ref so channel re-subscribes when args change (e.g. userId). */
  channelArgs?: MaybeRefOrGetter<Parameters<D["name"]>>;
  /** Legacy: raw channel name string — may be a getter/ref so channel re-subscribes reactively. */
  channelName?: MaybeRefOrGetter<string>;
  listener: (msg: T) => void;
  enabled?: MaybeRef<boolean>;
  options?: AblyChannelSyntheticOptions;
  validate?: AblyChannelValidateFn<T>;
}) {
  const {
    listener,
    enabled,
    options: _options,
    validate = ablyChannelValidateIdentityFn,
  } = args;
  const options = withAblyChannelSyntheticDefaultOptions(
    { rewind: 0 },
    _options,
  );
  const publishQueue = useQueue<T>();
  const registry = useAblyChannelRegistry();

  // shallowRef (not a plain let) so the queue-drain watch below re-tracks the
  // handle's isAttached after a reactive re-subscribe.
  const handleRef = shallowRef<MessageReaderHandle | null>(null);

  watchEffect((onCleanup) => {
    const disabled = toValue(enabled) === false;
    if (disabled) {
      handleRef.value = null;
      return;
    }

    // Resolve descriptor and channelArgs inside the watchEffect so that
    // reactive getters (e.g. () => [userStore.userId]) are tracked and
    // trigger re-subscription when their values change.
    const resolvedDescriptor = (args.descriptor ??
      makeRawDescriptor(toValue(args.channelName ?? ""))) as ChannelDescriptor<
      never,
      any[],
      "none"
    >;
    const resolvedChannelArgs = (toValue(args.channelArgs) ?? []) as Parameters<
      typeof resolvedDescriptor.name
    >;

    const h = registry.subscribeChannel({
      descriptor: resolvedDescriptor,
      channelArgs: resolvedChannelArgs,
      scope: getCurrentScope() ?? undefined,
    });
    handleRef.value = h;

    const topic = toValue(options.topic);
    function listenerWrapper(msg: Ably.Message) {
      const data = validate(msg.data);
      if (data?.value) listener(data.value);
    }

    const unsub = h.subscribe(topic, listenerWrapper);

    if (toValue(options.rewind) > 0) {
      rewindChannel(h, toValue(options.rewind), listenerWrapper);
    }

    onCleanup(() => {
      unsub();
      h.release();
      handleRef.value = null;
    });
  });

  // Drain publish queue when handle becomes attached (INV-10). Tracks
  // handleRef so it re-fires for a handle created by a reactive re-subscribe.
  watch(
    () => handleRef.value?.isAttached.value,
    (attached) => {
      const h = handleRef.value;
      if (attached && h) {
        const topic = toValue(options.topic);
        publishQueue.iterate((data) => h.publish(topic, data).catch(() => {}));
      }
    },
  );

  async function publish(data: T): Promise<void> {
    const h = handleRef.value;
    if (!h || !h.isAttached.value) {
      publishQueue.enqueue(data);
      return;
    }
    await h.publish(toValue(options.topic), data);
  }

  return {
    ready: computed(() => handleRef.value?.isAttached.value ?? false),
    publish,
    isAttached: computed(() => handleRef.value?.isAttached.value ?? false),
    channelState: computed(() => handleRef.value?.state.value ?? "initialized"),
    errorReason: computed(() => handleRef.value?.errorReason.value ?? null),
  };
}
